import type { CSSSettings, Editable, Font } from "shared-types";
import { useStore } from "vuex";

import { useEditor } from "@/composables/useEditor";

import type { EditableOverwrite } from "@/types/types";
import type { RootState } from "@/store/types";
import { CampaignGetters } from "@/store/modules/campaign";
import { EditorAction, EditorGetters } from "@/store/modules/editor";

import { TemplateOverwriteScopes } from "@/enums/overwrites";

export function useOverwrite() {
    const store = useStore<RootState>();

    const { selectedGroupValues, selectedGroupValuesIds, selectedLanguage } = useEditor();

    function getAffectingGroupValueIds(groupsAffectingEditable: string[]) {
        return groupsAffectingEditable.reduce<string[]>((acc, groupName) => {
            if (groupName in selectedGroupValues.value) {
                const group = selectedGroupValues.value[groupName];
                acc.push(group!.groupValueId);
            }

            return acc;
        }, []);
    }

    function getOverwrite(editable: Editable, groupsAffectingEditable: string[], templateId: string | null) {
        const relevantSelectedGroups = getAffectingGroupValueIds(groupsAffectingEditable);

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        return store.getters[EditorGetters.editableOverwriteValue](
            editable._id,
            templateId,
            relevantSelectedGroups
        ) as EditableOverwrite;
    }

    function getOverwriteMediaItem(editable: Editable, groupsAffectingEditable: string[], templateId: string | null) {
        return getOverwrite(editable, groupsAffectingEditable, templateId).mediaItem;
    }

    function getSavedOverwrite(
        editableId: string,
        templateId: string | null,
        language?: string,
        selectedGroupsIds?: string[]
    ) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        return store.getters[CampaignGetters.editableOverwriteValue](
            editableId,
            templateId,
            language ?? selectedLanguage.value,
            selectedGroupsIds ?? selectedGroupValuesIds.value
        ) as EditableOverwrite;
    }

    function isTemplateOverwrite(editable: Editable, groupsAffectingEditable: string[], templateId: string) {
        const overwrite = getOverwrite(editable, groupsAffectingEditable, templateId);

        return (
            TemplateOverwriteScopes.includes(overwrite.valueFromScope!) &&
            (overwrite.mediaItem != null || overwrite.value != null)
        );
    }

    async function removeOverwrite(overwriteId: string) {
        await store.dispatch(EditorAction.RemoveOverwrite, overwriteId);
    }

    function areSettingsChanged(
        newSettings: EditableOverwrite["settings"],
        savedSettings: EditableOverwrite["settings"]
    ) {
        const newKeys = Object.keys(newSettings ?? {}) as (keyof CSSSettings)[];
        const savedKeys = Object.keys(savedSettings ?? {}) as (keyof CSSSettings)[];

        if (newKeys.length !== savedKeys.length) return true;

        return newKeys.some(key => {
            const newValue = newSettings?.[key];
            const savedValue = savedSettings?.[key];

            if (key === "fontFamily") {
                return (newValue as Font | undefined)?._id !== (savedValue as Font | undefined)?._id;
            }

            return newValue !== savedValue;
        });
    }

    function shouldRemoveOverwrite(
        oldOverwrite: EditableOverwrite,
        newOverwrite: EditableOverwrite,
        savedOverwrite: EditableOverwrite | undefined
    ) {
        const { value, mediaItem, scope, settings } = newOverwrite;

        const hasSameScope = scope === oldOverwrite.scope;
        const hasValue = !!value;
        const hasMediaItem = !!mediaItem;
        const hasSettings = settings && Object.keys(settings).length > 0;
        const settingsAreChanged = areSettingsChanged(settings, savedOverwrite?.settings);

        return hasSameScope && !hasValue && !hasMediaItem && !hasSettings && !settingsAreChanged;
    }

    return {
        getOverwrite,
        getOverwriteMediaItem,
        getSavedOverwrite,
        removeOverwrite,
        isTemplateOverwrite,
        shouldRemoveOverwrite
    };
}
